import React, { useState, useEffect } from 'react';
import buxferTransactions from '../services/buxfer.service';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, TextField, Checkbox, Box, SvgIcon } from '@mui/material';
import { styled } from '@mui/system';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const CenteredContainer = styled(Container)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '20px',
});

function BuxferSyncPage() {
    const [accounts, setAccounts] = useState([]);
    const [envAccounts, setEnvAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deepSyncDays, setDeepSyncDays] = useState(30);
    const [syncing, setSyncing] = useState({}); // Track syncing status for each account
    const [selected, setSelected] = useState([]); // Track selected accounts
    const [orderBy, setOrderBy] = useState('user_name');
    const [orderDirection, setOrderDirection] = useState('DESC');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchAccounts = async () => {
        try {
            const data = await buxferTransactions.getUniqueUsernames(searchTerm, 1, 100, orderBy, orderDirection);
            setAccounts(data.result);
            setLoading(false);
        } catch (error) {
            setError(error);
            setLoading(false);
        }
    };

    const fetchEnvAccounts = async () => {
        try {
            const res = await buxferTransactions.getAccountsFromEnvUsers();
            const rows = res.result || [];

            // Group/aggregate per user_name to ensure identical shape
            const grouped = rows.reduce((acc, cur) => {
                const key = cur.user_name || cur.email || 'unknown';
                if (!acc[key]) acc[key] = [];
                acc[key].push(cur);
                return acc;
            }, {});

            const agg = Object.keys(grouped).map((user) => {
                const items = grouped[user];
                const lastTs = items
                    .map(i => i.last_export_sync || i.last_synced || i.last_synced_at || i.last_sync)
                    .filter(Boolean)
                    .map(t => new Date(t).getTime())
                    .sort((a, b) => b - a)[0] || null;
                const notSyncCount = items.reduce((s, it) => s + (it.is_synced === false ? 1 : 0), 0);
                return {
                    user_name: user,
                    notSyncingCount: notSyncCount,
                    last_export_sync: lastTs ? new Date(lastTs).toISOString() : null
                };
            });

            setEnvAccounts(agg);
        } catch (err) {
            console.error('Error fetching env accounts:', err);
        }
    };

    useEffect(() => {
        fetchAccounts();
        fetchEnvAccounts();
    }, [orderBy, orderDirection, searchTerm]);

    const handleSync = async (email) => {
        setSyncing(prev => ({ ...prev, [email]: true })); // Set syncing to true
        try {
            await buxferTransactions.syncAccount(email);
            await fetchAccounts();
            alert(`Account ${email} synced successfully!`);
        } catch (error) {
            console.error('Sync error:', error);
            alert(`Error syncing account ${email}: ${error.message}`);
        } finally {
            setSyncing(prev => ({ ...prev, [email]: false })); // Set syncing to false
        }
    };

    const handleDeepSync = async (email, days) => {
        setSyncing(prev => ({ ...prev, [email]: true })); // Set syncing to true
        try {
            await buxferTransactions.syncAccount(email, true, days);
            await fetchAccounts();
            alert(`Account ${email} deep synced successfully with ${days} days!`);
        } catch (error) {
            console.error('Deep Sync error:', error);
            alert(`Error deep syncing account ${email}: ${error.message}`);
        } finally {
            setSyncing(prev => ({ ...prev, [email]: false })); // Set syncing to false
        }
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = accounts.map((n) => n.user_name);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const handleSyncSelected = () => {
        selected.forEach(email => {
            handleSync(email);
        });
    };

    const handleDeepSyncSelected = () => {
        selected.forEach(email => {
            handleDeepSync(email, deepSyncDays);
        });
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    const handleSortClick = () => {
        setOrderDirection(orderBy === 'user_name' && orderDirection === 'ASC' ? 'DESC' : 'ASC');
        setOrderBy('user_name');
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    if (loading) {
        return (
            <CenteredContainer>
                <CircularProgress />
                <Typography>Loading accounts...</Typography>
            </CenteredContainer>
        );
    }

    if (error) {
        return (
            <CenteredContainer>
                <Typography color="error">Error: {error.message}</Typography>
            </CenteredContainer>
        );
    }

    return (
        <CenteredContainer>
            <Typography variant="h4" gutterBottom>Buxfer Data Import</Typography>
             <TextField
                label="Search Email"
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ marginBottom: '16px', width: '80%' }}
            />
            <TableContainer component={Paper} style={{ width: '80%' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selected.length > 0 && selected.length < accounts.length}
                                    checked={accounts.length > 0 && selected.length === accounts.length}
                                    onChange={handleSelectAllClick}
                                    inputProps={{
                                        'aria-label': 'select all accounts',
                                    }}
                                />
                            </TableCell>
                            <TableCell style={{ cursor: 'pointer' }} onClick={handleSortClick}>
                                Email
                                {orderBy === 'user_name' && (orderDirection === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />)}
                            </TableCell>
                            <TableCell align="center">Not Synced</TableCell>
                            <TableCell align="center">Last Successful Import</TableCell>
                            <TableCell align="center">API Source</TableCell>
                            <TableCell>Actions</TableCell>
                            <TableCell>Deep Sync</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {accounts.map((account) => {
                            const isItemSelected = isSelected(account.user_name);
                            const labelId = `enhanced-table-checkbox-${account.user_name}`;

                            return (
                                <TableRow
                                    hover
                                    onClick={(event) => handleClick(event, account.user_name)}
                                    role="checkbox"
                                    aria-checked={isItemSelected}
                                    tabIndex={-1}
                                    key={account.user_name}
                                    selected={isItemSelected}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={isItemSelected}
                                            inputProps={{ 'aria-labelledby': labelId }}
                                        />
                                    </TableCell>
                                    <TableCell component="th" id={labelId} scope="row">{account.user_name}</TableCell>
                                    <TableCell align="center">
                                        {account.notSyncingCount || 0} Account(s)
                                    </TableCell>
                                    <TableCell align="center">
                                        {account.last_export_sync 
                                            ? new Date(account.last_export_sync).toLocaleString() 
                                            : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {account.last_import_source ? account.last_import_source : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleSync(account.user_name)}
                                            disabled={syncing[account.user_name]}
                                            style={{ marginRight: '5px' }}
                                        >
                                            {syncing[account.user_name] ? <CircularProgress size={24} /> : 'Sync'}
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                onClick={() => handleDeepSync(account.user_name, deepSyncDays)}
                                                disabled={syncing[account.user_name]}
                                                style={{ marginRight: '5px' }}
                                            >
                                                {syncing[account.user_name] ? <CircularProgress size={24} /> : 'Deep Sync'}
                                            </Button>
                                            <TextField
                                                label="Days"
                                                type="number"
                                                defaultValue={deepSyncDays}
                                                onChange={(e) => setDeepSyncDays(parseInt(e.target.value, 10))}
                                                style={{ width: '60px' }}
                                                disabled={syncing[account.user_name]}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Env-derived twin table stacked under the main table. No header, only rows; separated by a thick border */}
            <TableContainer component={Paper} style={{ width: '80%', borderTop: '6px solid #ddd', marginTop: '-1px' }}>
                <Table>
                    <TableBody>
                        {envAccounts.map((account) => {
                            const isItemSelected = isSelected(account.user_name);
                            const labelId = `env-enhanced-table-checkbox-${account.user_name}`;

                            return (
                                <TableRow
                                    hover
                                    onClick={(event) => handleClick(event, account.user_name)}
                                    role="checkbox"
                                    aria-checked={isItemSelected}
                                    tabIndex={-1}
                                    key={`env-${account.user_name}`}
                                    selected={isItemSelected}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={isItemSelected}
                                            inputProps={{ 'aria-labelledby': labelId }}
                                        />
                                    </TableCell>
                                    <TableCell component="th" id={labelId} scope="row">{account.user_name}</TableCell>
                                    <TableCell align="center">
                                        {account.notSyncingCount || 0} Account(s)
                                    </TableCell>
                                    <TableCell align="center">
                                        {account.last_export_sync 
                                            ? new Date(account.last_export_sync).toLocaleString() 
                                            : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {account.last_import_source ? account.last_import_source : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleSync(account.user_name)}
                                            disabled={syncing[account.user_name]}
                                            style={{ marginRight: '5px' }}
                                        >
                                            {syncing[account.user_name] ? <CircularProgress size={24} /> : 'Sync'}
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                onClick={() => handleDeepSync(account.user_name, deepSyncDays)}
                                                disabled={syncing[account.user_name]}
                                                style={{ marginRight: '5px' }}
                                            >
                                                {syncing[account.user_name] ? <CircularProgress size={24} /> : 'Deep Sync'}
                                            </Button>
                                            <TextField
                                                label="Days"
                                                type="number"
                                                defaultValue={deepSyncDays}
                                                onChange={(e) => setDeepSyncDays(parseInt(e.target.value, 10))}
                                                style={{ width: '60px' }}
                                                disabled={syncing[account.user_name]}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box mt={2}>
                <TextField
                    label="Days for Selected"
                    type="number"
                    defaultValue={deepSyncDays}
                    onChange={(e) => setDeepSyncDays(parseInt(e.target.value, 10))}
                    style={{ width: '80px', marginRight: '10px' }}
                />
                <Button variant="contained" color="primary" onClick={handleSyncSelected} disabled={selected.length === 0}>
                    Sync Selected
                </Button>
                <Button variant="contained" color="secondary" onClick={handleDeepSyncSelected} disabled={selected.length === 0}>
                    Deep Sync Selected
                </Button>
            </Box>
        </CenteredContainer>
    );
}

export default BuxferSyncPage;
